import {
  Content,
  Item,
  ItemIndicator,
  Label as RadixLabel,
  Portal,
  RadioGroup,
  RadioItem,
  Root,
  Separator as RadixSeparator,
  Trigger,
} from '@radix-ui/react-dropdown-menu';
import { Slottable } from '@radix-ui/react-slot';
import { Check } from 'lucide-react';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '../lib/cn';
import type { IconComponent } from '../lib/types';

export const MenuRoot = Root;
export const MenuTrigger = Trigger;
/** Wraps `MenuRadioItem`s. Takes `value` and `onValueChange`. */
export const MenuRadioGroup = RadioGroup;

export interface MenuProps extends ComponentPropsWithRef<typeof Root> {
  /** The control that opens it. Wrapped in `MenuTrigger` with `asChild`, so
   *  pass a real button rather than a div. */
  trigger: ReactNode;
  /**
   * Required, and applied to both the trigger and the menu.
   *
   * A menu with no accessible name is announced as "menu" and nothing else, and
   * a project list renders one per row — so "menu" is what a screen reader user
   * hears twelve times with no way to tell them apart.
   *
   * The trigger needs it just as much: these are icon buttons, and an icon has
   * no accessible name at all. Radix's `asChild` merges the child's own props
   * last, so a trigger that labels itself still wins.
   */
  label: string;
  align?: 'start' | 'end';
  className?: string | undefined;
  /** Radix portals to document.body, outside any `.pl-on-map` subtree. */
  portalClassName?: string | undefined;
  children: ReactNode;
}

/**
 * A dropdown menu (P1-WEB-03, PRD §PROJ-10).
 *
 * The primitive `packages/ui` was missing. Two features already wanted it — the
 * project actions menu here, and `AUTH-8`'s account menu, which shipped as two
 * plain header controls in P0-WEB-08 for exactly this reason.
 *
 * Radix rather than a hand-rolled popover, like every other overlay in this
 * package: focus trapping, typeahead, arrow-key navigation and escape handling
 * are the whole of a menu's accessibility, and they are the parts that get cut
 * when a menu is written twice.
 */
export function Menu({
  trigger,
  label,
  align = 'end',
  className,
  portalClassName,
  children,
  ...props
}: MenuProps) {
  return (
    <Root {...props}>
      <Trigger asChild aria-label={label}>
        {trigger}
      </Trigger>
      <Portal>
        <div className={portalClassName}>
          <Content
            aria-label={label}
            align={align}
            sideOffset={6}
            className={cn(
              'z-(--pl-z-overlay) min-w-48 rounded-lg border border-line bg-surface p-1 shadow-overlay',
              'focus:outline-none',
              className,
            )}
          >
            {children}
          </Content>
        </div>
      </Portal>
    </Root>
  );
}

export interface MenuItemProps extends ComponentPropsWithRef<typeof Item> {
  icon?: IconComponent | undefined;
  /** Destructive actions read differently and are placed apart. */
  tone?: 'default' | 'danger' | undefined;
}

export function MenuItem({
  icon: Icon,
  tone = 'default',
  className,
  children,
  ...props
}: MenuItemProps) {
  return (
    <Item
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm outline-none select-none',
        'data-highlighted:bg-surface-raised',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        tone === 'danger' ? 'text-danger' : 'text-fg',
        className,
      )}
      {...props}
    >
      {Icon === undefined ? null : <Icon aria-hidden className="size-4 shrink-0" />}
      {/* Marks which child `asChild` merges onto, so the icon can sit beside
          it. Without this Slot sees two children and throws — the same reason
          Button uses it. */}
      <Slottable>{children}</Slottable>
    </Item>
  );
}

export function MenuSeparator() {
  return <RadixSeparator className="my-1 h-px bg-line" />;
}

/**
 * A heading for a group of items.
 *
 * Radix renders it as a real group label rather than a styled `div`, so a
 * screen reader announces "Organisation" before the options rather than
 * leaving five council names loose in a menu with no idea what they are.
 */
export function MenuLabel({
  className,
  children,
  ...props
}: ComponentPropsWithRef<typeof RadixLabel>) {
  return (
    <RadixLabel
      className={cn('px-2.5 py-1.5 text-xs font-medium text-fg-muted', className)}
      {...props}
    >
      {children}
    </RadixLabel>
  );
}

/**
 * One choice in a `MenuRadioGroup`.
 *
 * A radio rather than a checked `MenuItem` because the difference is audible:
 * this announces the selected state and the set it belongs to, where a check
 * icon on a plain item is decoration a screen reader never mentions.
 *
 * Space is reserved for the indicator on every item, selected or not, so the
 * labels line up instead of shifting sideways as the selection moves.
 */
export function MenuRadioItem({
  className,
  children,
  ...props
}: ComponentPropsWithRef<typeof RadioItem>) {
  return (
    <RadioItem
      className={cn(
        'relative flex cursor-pointer items-center rounded-md py-2 pr-2.5 pl-8 text-sm text-fg outline-none select-none',
        'data-highlighted:bg-surface-raised',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <ItemIndicator className="absolute left-2.5 inline-flex items-center">
        <Check aria-hidden className="size-3.5" />
      </ItemIndicator>
      {children}
    </RadioItem>
  );
}
